import { IDocumentFileModel, IDocumentFolderModel } from '@api/models';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import OverflowTooltip from '@components/OverflowTooltip/OverflowTooltip';
import useBoundingClientRect from '@hooks/use-bounding-client-rect';
import { ChevronRightIcon, FolderIcon } from '@icon/index';
import { Input, Label } from '@library/forms';
import { getFileExtension } from 'modules/shared/utils/file-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type TableItem =
    | { type: 'folder'; data: IDocumentFolderModel }
    | { type: 'file'; data: IDocumentFileModel }
    | { type: 'highlight'; data: { id: string; name: string; label?: string } };

const buildChildrenMap = (folders: IDocumentFolderModel[]) => {
    const map = new Map<string | null, IDocumentFolderModel[]>();

    for (const f of folders) {
        const pid = (f.parentId ?? null) as string | null;

        if (!map.has(pid)) map.set(pid, []);
        map.get(pid)!.push(f);
    }

    for (const [k, arr] of map.entries()) {
        arr.sort((a, b) => a.name.localeCompare(b.name));
        map.set(k, arr);
    }

    return map;
};

const buildByIdMap = (folders: IDocumentFolderModel[]) => {
    const map = new Map<string, IDocumentFolderModel>();

    for (const f of folders) {
        map.set(f._id as string, f);
    }

    return map;
};

const getBreadcrumb = (byId: Map<string, IDocumentFolderModel>, parentId: string | null) => {
    const path: IDocumentFolderModel[] = [];
    let curr = parentId ? byId.get(parentId) : undefined;

    while (curr) {
        path.unshift(curr);
        curr = curr.parentId ? byId.get(curr.parentId as string) : undefined;
    }

    return path;
};

type FolderDestinationPickerProps = {
    folders: IDocumentFolderModel[];
    files?: IDocumentFileModel[];
    value: string | null;
    onChange: (next: string | null) => void;

    excludeFolderIds?: Set<string>;

    currentLocationParentId?: string | null;
    initialBrowseParentId?: string | null;

    highlightFolderId?: string | null;
    highlightLabel?: string;
};

const FolderRow: React.FC<{
    title: string;
    subtitle?: string;
    onNavigate?: () => void;
    isMuted?: boolean;
}> = ({ title, subtitle, onNavigate, isMuted }) => {
    return (
        <div
            role="button"
            tabIndex={0}
            className={twMerge(
                'flex items-center gap-[10px] pl-3 h-11 cursor-pointer hover:bg-grayscale-25',
                isMuted && 'opacity-70'
            )}
            onClick={onNavigate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate?.();
                }
            }}
        >
            <div className="flex items-center gap-2 select-none flex-1 overflow-hidden">
                <div>
                    <FolderIcon width={24} height={16} />
                </div>

                <div className="min-w-0 flex-1 overflow-hidden">
                    <OverflowTooltip className="font-semibold text-sm">{title}</OverflowTooltip>
                    {subtitle ? <div className="text-xs text-grayscale-500 truncate">{subtitle}</div> : null}
                </div>
            </div>

            <button
                type="button"
                className="flex items-center justify-center w-10 h-full ml-auto text-grayscale-400 hover:text-grayscale-700"
                onClick={(e) => {
                    e.stopPropagation();
                    onNavigate?.();
                }}
            >
                <ChevronRightIcon width={7} height={10} />
            </button>
        </div>
    );
};

const FileRow: React.FC<{
    file: IDocumentFileModel;
}> = ({ file }) => {
    return (
        <div className="flex items-center gap-[10px] pl-3 h-11 select-none">
            <div className="flex items-center gap-2 flex-1 overflow-hidden">
                <div>
                    <DocFileIcon className="w-4" extension={getFileExtension(file.path)} />
                </div>

                <OverflowTooltip className="font-semibold text-sm">{file.name}</OverflowTooltip>
            </div>
        </div>
    );
};

const FolderDestinationPicker: React.FC<FolderDestinationPickerProps> = ({
    folders,
    files = [],
    value,
    onChange,
    excludeFolderIds,
    currentLocationParentId = null,
    initialBrowseParentId,
    highlightFolderId,
    highlightLabel,
}) => {
    console.log('excludeFolderIds', excludeFolderIds);
    const [topRefSetter, topBoundingClientRect] = useBoundingClientRect();
    const [bottomRefSetter, bottomBoundingClientRect] = useBoundingClientRect();

    const childrenMap = useMemo(() => buildChildrenMap(folders), [folders]);
    const byId = useMemo(() => buildByIdMap(folders), [folders]);

    const [currentParentId, setCurrentParentId] = useState<string | null>(initialBrowseParentId ?? value ?? null);

    const selectedFolderName = useMemo(() => {
        if (!value) return 'All Documents';
        return byId.get(value)?.name ?? 'All Documents';
    }, [value, byId]);

    const rawFolders = useMemo(() => {
        return childrenMap.get(currentParentId) ?? [];
    }, [childrenMap, currentParentId]);

    const currentChildrenFolders = useMemo(() => {
        if (!excludeFolderIds || excludeFolderIds.size === 0) return rawFolders;
        return rawFolders.filter((f) => !excludeFolderIds.has(f._id as string));
    }, [rawFolders, excludeFolderIds]);

    const currentLevelFiles = useMemo(() => {
        return files.filter((f) => (f.folder ?? null) === currentParentId);
    }, [files, currentParentId]);

    const breadcrumb = useMemo(() => {
        const path = getBreadcrumb(byId, currentParentId);
        return [{ name: 'All Documents', _id: null as any }, ...path];
    }, [byId, currentParentId]);

    const highlightRow = useMemo(() => {
        if (!highlightFolderId) return null;

        const folder = byId.get(highlightFolderId);
        if (!folder) return null;

        if ((folder.parentId ?? null) !== currentParentId) return null;

        return {
            type: 'highlight' as const,
            data: {
                id: highlightFolderId,
                name: folder.name,
                label: highlightLabel,
            },
        };
    }, [highlightFolderId, highlightLabel, byId, currentParentId]);

    const tableItems = useMemo(() => {
        const folderItems: TableItem[] = currentChildrenFolders.map((folder) => ({
            type: 'folder',
            data: folder,
        }));

        const fileItems: TableItem[] = currentLevelFiles.map((file) => ({
            type: 'file',
            data: file,
        }));

        const items = [...folderItems];

        if (highlightRow) items.push(highlightRow);

        items.push(...fileItems);

        return items;
    }, [currentChildrenFolders, currentLevelFiles, highlightRow]);

    const navigate = (id: string | null) => {
        setCurrentParentId(id);
        onChange(id);
    };

    const selectCurrentBrowseLocation = () => {
        onChange(currentParentId);
    };

    return (
        <div>
            <div ref={topRefSetter}>
                <Label>Selected Folder</Label>
                <Input placeholder="Select Folder" className="mb-2" value={selectedFolderName} readOnly />
            </div>

            {/* <ShadowScrollContainer
                maxHeight={`calc(100vh - ${topBoundingClientRect.top}px - ${bottomBoundingClientRect.height}px - 140px - 16px)`}
            > */}
            <div className="border rounded-[10px] divide-y divide-grayscale-200 overflow-hidden">
                <ul aria-label="breadcrumb" className="flex items-center py-2 px-3">
                    {breadcrumb.map((row, i) => (
                        <li
                            key={i}
                            className={twMerge(
                                'flex items-center overflow-hidden text-xs',
                                i === breadcrumb.length - 1
                                    ? 'text-grayscale-900 font-semibold after:hidden'
                                    : "text-grayscale-500 after:mx-2 after:text-xs after:font-medium after:text-grayscale-400 after:content-['/']"
                            )}
                        >
                            {i < breadcrumb.length - 1 ? (
                                <button
                                    type="button"
                                    className="cursor-pointer hover:underline overflow-hidden"
                                    onClick={() => navigate(row._id ?? null)}
                                >
                                    <OverflowTooltip>{row.name}</OverflowTooltip>
                                </button>
                            ) : (
                                <OverflowTooltip>{row.name}</OverflowTooltip>
                            )}
                        </li>
                    ))}
                </ul>

                <div
                    role="button"
                    tabIndex={0}
                    className="px-3 py-2 text-xs text-grayscale-500 cursor-pointer hover:bg-grayscale-25"
                    onClick={selectCurrentBrowseLocation}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectCurrentBrowseLocation();
                        }
                    }}
                >
                    Move here {currentParentId === currentLocationParentId ? '(Current location)' : ''}
                </div>

                {tableItems.length === 0 ? (
                    <div className="flex items-center justify-center h-20 text-sm text-grayscale-400">
                        No files available
                    </div>
                ) : (
                    tableItems.map((item) => {
                        if (item.type === 'folder') {
                            const id = item.data._id as string;

                            return (
                                <FolderRow
                                    key={`folder-${id}`}
                                    title={item.data.name}
                                    onNavigate={() => navigate(id)}
                                />
                            );
                        }

                        if (item.type === 'highlight') {
                            return (
                                <FolderRow
                                    key={`highlight-${item.data.id}`}
                                    title={item.data.name}
                                    subtitle={item.data.label}
                                    isMuted
                                    onNavigate={() => null}
                                />
                            );
                        }

                        return <FileRow key={`file-${item.data._id}`} file={item.data} />;
                    })
                )}
            </div>
            {/* </ShadowScrollContainer> */}

            <div ref={bottomRefSetter} />
        </div>
    );
};

export default FolderDestinationPicker;
